import React, { useEffect, useState, useCallback } from "react";
import { appVersionsApi } from "../../services/api";
import { PageHeader, Input, Button, Toggle, Modal } from "../../components/ui";
import toast from "react-hot-toast";
import { showToast } from "../../utils/toast";
import {
  AlertTriangle,
  Wrench,
  RefreshCw,
  Smartphone,
  Apple,
  Pencil,
  Trash2,
  Plus,
  Tag,
  ChevronsDown,
  MessageSquare,
  ExternalLink,
  Info,
  CheckCircle,
  XCircle,
} from "lucide-react";

const PLATFORMS = ["ANDROID", "IOS"];

const AndroidIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="#59f420" aria-hidden="true">
    <path d="M17.6 9.48l1.43-2.48a.5.5 0 1 0-.87-.5l-1.46 2.53A8.98 8.98 0 0 0 12 7.75c-1.68 0-3.25.46-4.57 1.28L5.97 6.5a.5.5 0 1 0-.87.5L6.53 9.48A8.96 8.96 0 0 0 3 16v3a1 1 0 0 0 1 1h1v2.25a1.25 1.25 0 1 0 2.5 0V20h9v2.25a1.25 1.25 0 1 0 2.5 0V20h1a1 1 0 0 0 1-1v-3a8.96 8.96 0 0 0-3.4-6.52ZM8.5 13.25a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Zm7 0a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
  </svg>
);

const AppleIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill="#d9d556" aria-hidden="true">
    <path d="M11.182.008c0 1.778-1.341 3.128-2.542 3.128-.153-1.59 1.377-3.128 2.542-3.128Zm2.885 11.33c-.37.86-.547 1.243-1.022 1.986-.664 1.04-1.602 2.334-2.765 2.345-1.033.01-1.3-.673-2.701-.665-1.4.008-1.695.678-2.728.668-1.163-.011-2.051-1.18-2.715-2.22C.278 12.442-.95 8.62.744 5.996c.829-1.285 2.31-2.04 3.702-2.04 1.418 0 2.313.677 3.487.677 1.14 0 1.836-.678 3.477-.678 1.24 0 2.556.675 3.385 1.84-3.065 1.682-2.568 6.04-.728 7.542Z" />
  </svg>
);

const PLATFORM_META = {
  ANDROID: {
    icon: <AndroidIcon size={22} />,
    label: "Android",
    storeLabel: "Play Store URL",
    storePlaceholder: "https://play.google.com/store/apps/details?id=...",
  },
  IOS: {
    icon: <AppleIcon size={20} />,
    label: "iOS",
    storeLabel: "App Store URL",
    storePlaceholder: "https://apps.apple.com/app/id...",
  },
};

const EMPTY_FORM = {
  platform: "",
  latestVersion: "",
  minimumSupportedVersion: "",
  forceUpdate: false,
  updateMessage: "",
  storeUrl: "",
  maintenance: false,
  softUpdate: false,
};

const semverRegex = /^\d+\.\d+\.\d+$/;

function semverLte(a, b) {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    if (pa[i] < pb[i]) return true;
    if (pa[i] > pb[i]) return false;
  }
  return true;
}

// ── Confirm Force Update Modal ────────────────────────────────────────────────
function ConfirmForceUpdateModal({ open, platformLabel, onClose, onConfirm, loading }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "var(--bg-card)", borderRadius: "var(--radius-lg)", boxShadow: "0 24px 64px rgba(0,0,0,0.25)", zIndex: 1101, padding: "28px 32px", width: 420, maxWidth: "90vw" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <AlertTriangle size={28} strokeWidth={1.5} color="#dc2626" />
        </div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", marginBottom: 8 }}>
          Enable Force Update?
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", marginBottom: 24, lineHeight: 1.6 }}>
          This will force all users on{" "}
          <strong style={{ color: "var(--text-primary)" }}>{platformLabel}</strong>{" "}
          to update the app immediately. This should only be used for critical fixes.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "none", background: "#dc2626", color: "#fff", fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.65 : 1 }}>
            {loading ? "Enabling…" : "Enable"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Confirm Maintenance Modal ─────────────────────────────────────────────────
function ConfirmMaintenanceModal({ open, platformLabel, onClose, onConfirm, loading }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "var(--bg-card)", borderRadius: "var(--radius-lg)", boxShadow: "0 24px 64px rgba(0,0,0,0.25)", zIndex: 1101, padding: "28px 32px", width: 420, maxWidth: "90vw" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <Wrench size={28} strokeWidth={1.5} color="#d97706" />
        </div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", marginBottom: 8 }}>
          Enable Maintenance Mode?
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", marginBottom: 24, lineHeight: 1.6 }}>
          This will show a maintenance screen to all users on{" "}
          <strong style={{ color: "var(--text-primary)" }}>{platformLabel}</strong>.
          Users will not be able to access the app until this is disabled.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "none", background: "#d97706", color: "#fff", fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.65 : 1 }}>
            {loading ? "Enabling…" : "Enable"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Confirm Soft Update Modal ─────────────────────────────────────────────────
function ConfirmSoftUpdateModal({ open, platformLabel, onClose, onConfirm, loading }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "var(--bg-card)", borderRadius: "var(--radius-lg)", boxShadow: "0 24px 64px rgba(0,0,0,0.25)", zIndex: 1101, padding: "28px 32px", width: 420, maxWidth: "90vw" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <RefreshCw size={28} strokeWidth={1.5} color="#2563eb" />
        </div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", marginBottom: 8 }}>
          Enable Soft Update?
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", marginBottom: 24, lineHeight: 1.6 }}>
          Do you want to enable soft update for{" "}
          <strong style={{ color: "var(--text-primary)" }}>{platformLabel}</strong>
          ? Users will be prompted to update, but they can still continue using the app.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 20px", borderRadius: "var(--radius)", border: "none", background: "#2563eb", color: "#fff", fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.65 : 1 }}>
            {loading ? "Enabling…" : "Enable"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Platform Card ─────────────────────────────────────────────────────────────
function PlatformCard({ platform, data, onEdit, onDelete, onToggleForce, onToggleMaintenance, onToggleSoftUpdate, onAdd }) {
  const meta = PLATFORM_META[platform];
  const [togglingForce, setTogglingForce] = useState(false);
  const [togglingMaint, setTogglingMaint] = useState(false);
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
  const [maintConfirmOpen, setMaintConfirmOpen] = useState(false);
  const [togglingSoft, setTogglingSoft] = useState(false);
  const [softConfirmOpen, setSoftConfirmOpen] = useState(false);

  if (!data) {
    return (
      <div style={{ border: "2px dashed var(--border)", borderRadius: 14, padding: 32, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, minHeight: 280, background: "var(--bg-card)" }}>
        <span style={{ fontSize: 36 }}>{meta.icon}</span>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>{meta.label}</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>No version configured yet</div>
        <Button size="sm" onClick={() => onAdd(platform)}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Plus size={13} strokeWidth={2.5} /> Add {meta.label}
          </span>
        </Button>
      </div>
    );
  }

  const handleForceToggle = async (val) => {
    if (!val) { setTogglingForce(true); await onToggleForce(data.id, false); setTogglingForce(false); return; }
    setForceConfirmOpen(true);
  };

  const confirmForceUpdate = async () => {
    try { setTogglingForce(true); await onToggleForce(data.id, true); setForceConfirmOpen(false); }
    catch (err) { showToast(err?.response?.data?.message || "Failed to enable force update", "error"); }
    finally { setTogglingForce(false); }
  };

  const handleMaintToggle = async (val) => {
    if (!val) { setTogglingMaint(true); await onToggleMaintenance(data.id, false); setTogglingMaint(false); return; }
    setMaintConfirmOpen(true);
  };

  const confirmMaintenance = async () => {
    try { setTogglingMaint(true); await onToggleMaintenance(data.id, true); setMaintConfirmOpen(false); }
    catch (err) { showToast(err?.response?.data?.message || "Failed to enable maintenance mode", "error"); }
    finally { setTogglingMaint(false); }
  };

  const handleSoftToggle = async (val) => {
    if (!val) { try { setTogglingSoft(true); await onToggleSoftUpdate(data.id, false); } finally { setTogglingSoft(false); } return; }
    setSoftConfirmOpen(true);
  };

  const confirmSoftUpdate = async () => {
    try { setTogglingSoft(true); await onToggleSoftUpdate(data.id, true); setSoftConfirmOpen(false); }
    catch (err) { showToast(err?.response?.data?.message || "Failed to enable soft update", "error"); }
    finally { setTogglingSoft(false); }
  };

  const borderColor = data.maintenance ? "rgba(234,179,8,0.45)" : data.forceUpdate ? "rgba(239,68,68,0.35)" : "var(--border)";
  const cardShadow  = data.maintenance ? "0 0 0 3px rgba(234,179,8,0.10)" : data.forceUpdate ? "0 0 0 3px rgba(239,68,68,0.08)" : "none";

  return (
    <div style={{ border: `1px solid ${borderColor}`, borderRadius: 14, background: "var(--bg-card)", overflow: "hidden", transition: "border-color 0.2s", boxShadow: cardShadow }}>

      {/* ── Card Header ── */}
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", background: "rgba(15,61,62,0.04)", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 22 }}>{meta.icon}</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>{meta.label}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
            Last updated:{" "}
            {new Date(data.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {data.maintenance && (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e", background: "rgba(234,179,8,0.18)", padding: "2px 10px", borderRadius: 20, border: "1px solid rgba(234,179,8,0.3)", display: "flex", alignItems: "center", gap: 4 }}>
              <Wrench size={11} strokeWidth={2.5} /> Maintenance
            </span>
          )}
          {data.softUpdate && (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", background: "rgba(59,130,246,0.12)", padding: "2px 10px", borderRadius: 20, border: "1px solid rgba(59,130,246,0.25)", display: "flex", alignItems: "center", gap: 4 }}>
              <RefreshCw size={11} strokeWidth={2.5} /> Soft Update
            </span>
          )}
          {data.active ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#22c55e", background: "rgba(34,197,94,0.1)", padding: "2px 10px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}>
              <CheckCircle size={11} strokeWidth={2.5} /> Active
            </span>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", background: "rgba(239,68,68,0.1)", padding: "2px 10px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}>
              <XCircle size={11} strokeWidth={2.5} /> Inactive
            </span>
          )}
        </div>
      </div>

      {/* ── Confirm Modals ── */}
      <ConfirmForceUpdateModal open={forceConfirmOpen} platformLabel={meta.label} onClose={() => setForceConfirmOpen(false)} onConfirm={confirmForceUpdate} loading={togglingForce} />
      <ConfirmMaintenanceModal open={maintConfirmOpen} platformLabel={meta.label} onClose={() => setMaintConfirmOpen(false)} onConfirm={confirmMaintenance} loading={togglingMaint} />
      <ConfirmSoftUpdateModal  open={softConfirmOpen}  platformLabel={meta.label} onClose={() => setSoftConfirmOpen(false)}  onConfirm={confirmSoftUpdate}  loading={togglingSoft}  />

      {/* ── Alert Banners ── */}
      {data.forceUpdate && (
        <div style={{ padding: "8px 16px", background: "rgba(245,237,237,0.08)", borderBottom: "1px solid rgba(153,147,147,0.15)", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#7f1d1d", fontWeight: 600 }}>
          <AlertTriangle size={13} strokeWidth={2.5} /> Force Update is ON — all users must update immediately
        </div>
      )}
      {data.maintenance && (
        <div style={{ padding: "8px 16px", background: "rgba(254,252,232,0.06)", borderBottom: "1px solid rgba(234,179,8,0.18)", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#78350f", fontWeight: 600 }}>
          <Wrench size={13} strokeWidth={2.5} /> Maintenance Mode is ON — app is temporarily unavailable to users
        </div>
      )}
      {data.softUpdate && (
        <div style={{ padding: "8px 16px", background: "rgba(239,246,255,0.06)", borderBottom: "1px solid rgba(59,130,246,0.18)", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#1e3a8a", fontWeight: 600 }}>
          <RefreshCw size={13} strokeWidth={2.5} /> Soft Update is ON — users will be prompted to update
        </div>
      )}

      {/* ── Card Body ── */}
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Version tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { label: "Latest Version",  value: data.latestVersion,           icon: <Tag size={12} strokeWidth={2.5} /> },
            { label: "Min. Supported",  value: data.minimumSupportedVersion, icon: <ChevronsDown size={12} strokeWidth={2.5} /> },
          ].map(({ label, value, icon }) => (
            <div key={label} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--bg-input)", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                {icon} {label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#0F3D3E", fontFamily: "monospace" }}>
                {value || "—"}
              </div>
            </div>
          ))}
        </div>

        {/* Update Message */}
        <div style={{ padding: "10px 12px", borderRadius: 8, background: "var(--bg-input)", border: "1px solid var(--border)", fontSize: 13, color: "var(--text-secondary)" }}>
          <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
            <MessageSquare size={12} strokeWidth={2.5} /> Update Message
          </span>
          {data.updateMessage || <span style={{ color: "var(--text-muted)" }}>—</span>}
        </div>

        {/* Store URL */}
        {data.storeUrl && (
          <a
            href={data.storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#0F3D3E", textDecoration: "none", fontWeight: 600, padding: "8px 12px", borderRadius: 8, background: "rgba(15,61,62,0.06)", border: "1px solid rgba(15,61,62,0.15)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(15,61,62,0.12)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(15,61,62,0.06)")}
          >
            <ExternalLink size={13} strokeWidth={2.5} /> {meta.storeLabel}
            <ExternalLink size={11} strokeWidth={2.5} style={{ marginLeft: "auto" }} />
          </a>
        )}

        {/* ── Force Update Toggle ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 8, border: `1px solid ${data.forceUpdate ? "rgba(239,68,68,0.30)" : "var(--border)"}`, background: data.forceUpdate ? "rgba(254,242,242,0.06)" : "var(--bg-input)", transition: "all 0.2s", opacity: togglingForce ? 0.6 : 1 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: data.forceUpdate ? "#7f1d1d" : "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={14} strokeWidth={2.5} /> Force Update
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
              {data.forceUpdate ? "Users must update to continue" : "Update is optional"}
            </div>
          </div>
          <Toggle value={!!data.forceUpdate} onChange={handleForceToggle} disabled={togglingForce} />
        </div>

        {/* ── Soft Update Toggle ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 8, border: `1px solid ${data.softUpdate ? "rgba(59,130,246,0.35)" : "var(--border)"}`, background: data.softUpdate ? "rgba(239,246,255,0.06)" : "var(--bg-input)", transition: "all 0.2s", opacity: togglingSoft ? 0.6 : 1 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: data.softUpdate ? "#1d4ed8" : "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
              <RefreshCw size={14} strokeWidth={2.5} /> Soft Update
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
              {data.softUpdate ? "Users are prompted to update" : "No soft update prompt"}
            </div>
          </div>
          <Toggle value={!!data.softUpdate} onChange={handleSoftToggle} disabled={togglingSoft} />
        </div>

        {/* ── Actions ── */}
        <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
          <Button size="sm" onClick={() => onEdit(data)} style={{ flex: 1 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <Pencil size={12} strokeWidth={2.5} /> Edit
            </span>
          </Button>
          <button
            onClick={() => {
              if (!window.confirm(`Delete ${meta.label} version? This cannot be undone.`)) return;
              onDelete(data.id);
            }}
            style={{ padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.06)", color: "#dc2626", transition: "all 0.15s", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239,68,68,0.12)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(239,68,68,0.06)")}
          >
            <Trash2 size={14} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Version Form Modal ────────────────────────────────────────────────────────
function VersionModal({ open, onClose, onSuccess, initial }) {
  const isEdit = !!initial?.id;
  const [form, setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formForceConfirmOpen, setFormForceConfirmOpen] = useState(false);
  const [formMaintConfirmOpen, setFormMaintConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              platform:                initial.platform                || "",
              latestVersion:           initial.latestVersion           || "",
              minimumSupportedVersion: initial.minimumSupportedVersion || "",
              forceUpdate:             initial.forceUpdate             ?? false,
              updateMessage:           initial.updateMessage           || "",
              storeUrl:                initial.storeUrl                || "",
              maintenance:             initial.maintenance             ?? false,
              softUpdate:              initial.softUpdate              ?? false,
            }
          : EMPTY_FORM
      );
    }
  }, [open, initial]);

  const f = (key) => ({
    value:    form[key],
    onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })),
  });

  const validate = () => {
    if (!isEdit && !PLATFORMS.includes(form.platform)) { toast.error("Platform must be ANDROID or IOS"); return false; }
    if (!semverRegex.test(form.latestVersion))           { toast.error("Latest version must be semver e.g. 1.0.2"); return false; }
    if (!semverRegex.test(form.minimumSupportedVersion)) { toast.error("Min supported version must be semver e.g. 1.0.0"); return false; }
    if (!semverLte(form.minimumSupportedVersion, form.latestVersion)) { toast.error("Min supported version must be ≤ latest version"); return false; }
    if (form.storeUrl && !/^https?:\/\/.+/.test(form.storeUrl)) { toast.error("Store URL must be a valid URL"); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        platform: form.platform, latestVersion: form.latestVersion,
        minimumSupportedVersion: form.minimumSupportedVersion,
        forceUpdate: form.forceUpdate, updateMessage: form.updateMessage,
        storeUrl: form.storeUrl, maintenance: form.maintenance, softUpdate: form.softUpdate,
      };
      if (isEdit) { await appVersionsApi.update(initial.id, payload); toast.success("Version updated"); }
      else        { await appVersionsApi.create(payload);             toast.success("Version created"); }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    }
    setSaving(false);
  };

  const meta = PLATFORM_META[form.platform] || {};

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Edit ${PLATFORM_META[initial?.platform]?.label || ""} Version` : "Add App Version"} width={480}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 13 }}>

        {/* Platform selector — create only */}
        {!isEdit && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", display: "block", marginBottom: 8 }}>
              Platform
            </label>
            <div style={{ display: "flex", gap: 10 }}>
              {PLATFORMS.map((p) => {
                const m = PLATFORM_META[p];
                const selected = form.platform === p;
                return (
                  <button
                    key={p} type="button"
                    onClick={() => setForm((f) => ({ ...f, platform: p }))}
                    style={{ flex: 1, padding: "10px", borderRadius: 10, cursor: "pointer", border: `2px solid ${selected ? "#0F3D3E" : "var(--border)"}`, background: selected ? "rgba(15,61,62,0.08)" : "var(--bg-input)", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, transition: "all 0.15s" }}
                  >
                    <span style={{ fontSize: 22 }}>{m.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: selected ? 700 : 500, color: selected ? "#0F3D3E" : "var(--text-secondary)" }}>{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="Latest Version"  placeholder="1.0.2" {...f("latestVersion")}           required />
          <Input label="Min. Supported"  placeholder="1.0.0" {...f("minimumSupportedVersion")} required />
        </div>

        <Input label="Update Message" placeholder="New update available" {...f("updateMessage")} />
        <Input label={meta.storeLabel || "Store URL"} type="url" placeholder={meta.storePlaceholder || "https://..."} {...f("storeUrl")} />

        {/* Force Update */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 8, border: `1px solid ${form.forceUpdate ? "rgba(239,68,68,0.3)" : "var(--border)"}`, background: form.forceUpdate ? "rgba(254,242,242,0.06)" : "var(--bg-input)" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: form.forceUpdate ? "#dc2626" : "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={14} strokeWidth={2.5} /> Force Update
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>Forces all users to update before using the app</div>
          </div>
          <Toggle value={form.forceUpdate} onChange={(v) => { if (!v) { setForm((f) => ({ ...f, forceUpdate: false })); return; } setFormForceConfirmOpen(true); }} />
        </div>

        {/* Maintenance */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 8, border: `1px solid ${form.maintenance ? "rgba(234,179,8,0.35)" : "var(--border)"}`, background: form.maintenance ? "rgba(254,252,232,0.06)" : "var(--bg-input)" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: form.maintenance ? "#0b0606" : "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
              <Wrench size={14} strokeWidth={2.5} /> Maintenance Mode
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>Temporarily disables app access for all users</div>
          </div>
          <Toggle value={form.maintenance} onChange={(v) => { if (!v) { setForm((f) => ({ ...f, maintenance: false })); return; } setFormMaintConfirmOpen(true); }} />
        </div>

        {/* Soft Update */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 8, border: `1px solid ${form.softUpdate ? "rgba(59,130,246,0.35)" : "var(--border)"}`, background: form.softUpdate ? "rgba(239,246,255,0.06)" : "var(--bg-input)" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: form.softUpdate ? "#1d4ed8" : "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
              <RefreshCw size={14} strokeWidth={2.5} /> Soft Update
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>Prompts users to update without blocking access</div>
          </div>
          <Toggle value={form.softUpdate} onChange={(v) => setForm((f) => ({ ...f, softUpdate: v }))} />
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : isEdit ? "Save Changes" : "Create"}</Button>
        </div>
      </form>

      <ConfirmForceUpdateModal
        open={formForceConfirmOpen}
        platformLabel={PLATFORM_META[form.platform]?.label || form.platform}
        onClose={() => setFormForceConfirmOpen(false)}
        onConfirm={() => { setForm((f) => ({ ...f, forceUpdate: true })); setFormForceConfirmOpen(false); }}
        loading={false}
      />
      <ConfirmMaintenanceModal
        open={formMaintConfirmOpen}
        platformLabel={PLATFORM_META[form.platform]?.label || form.platform}
        onClose={() => setFormMaintConfirmOpen(false)}
        onConfirm={() => { setForm((f) => ({ ...f, maintenance: true })); setFormMaintConfirmOpen(false); }}
        loading={false}
      />
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AppVersions() {
  const [versions, setVersions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState({ open: false, data: null });
  const [globalMaintLoading,     setGlobalMaintLoading]     = useState(false);
  const [globalMaintConfirmOpen, setGlobalMaintConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await appVersionsApi.getAll();
      setVersions(r.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load versions");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const byPlatform = (p) => versions.find((v) => v.platform === p) || null;

  const handleToggleForce = async (id, val) => {
    try { await appVersionsApi.toggleForce(id, val); toast.success(val ? "Force update enabled" : "Force update disabled"); load(); }
    catch (err) { toast.error(err.response?.data?.message || "Failed to toggle force update"); }
  };

  const handleToggleMaintenance = async (id, val) => {
    try { await appVersionsApi.toggleMaintenance(id, val); toast.success(val ? "Maintenance mode enabled" : "Maintenance mode disabled"); load(); }
    catch (err) { toast.error(err.response?.data?.message || "Failed to toggle maintenance"); }
  };

  const handleDelete = async (id) => {
    try { await appVersionsApi.delete(id); toast.success("Version deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.message || "Failed to delete"); }
  };

  const handleToggleSoftUpdate = async (id, val) => {
    try { await appVersionsApi.toggleSoftUpdate(id, val); toast.success(val ? "Soft update enabled" : "Soft update disabled"); await load(); }
    catch (err) { toast.error(err.response?.data?.message || "Failed to toggle soft update"); throw err; }
  };

  const versionItems = versions.filter((v) => PLATFORMS.includes(v.platform));
  const globalMaintenanceValue = versionItems.length > 0 && versionItems.every((v) => v.maintenance);

  const handleGlobalMaintenanceToggle = async (val) => {
    if (!versionItems.length) { showToast("No platform versions found", "error"); return; }
    if (val) { setGlobalMaintConfirmOpen(true); return; }
    try {
      setGlobalMaintLoading(true);
      await Promise.all(versionItems.map((v) => appVersionsApi.toggleMaintenance(v.id, false)));
      toast.success("Maintenance mode disabled for all platforms");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to disable maintenance mode");
    } finally { setGlobalMaintLoading(false); }
  };

  const confirmGlobalMaintenanceEnable = async () => {
    try {
      setGlobalMaintLoading(true);
      await Promise.all(versionItems.map((v) => appVersionsApi.toggleMaintenance(v.id, true)));
      setGlobalMaintConfirmOpen(false);
      toast.success("Maintenance mode enabled for all platforms");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to enable maintenance mode");
    } finally { setGlobalMaintLoading(false); }
  };

  const forceCount = versions.filter((v) => v.forceUpdate).length;
  const maintCount = versions.filter((v) => v.maintenance).length;
  const softUpdatePlatforms     = versions.filter((v) => v.softUpdate);
  const softUpdateCount         = softUpdatePlatforms.length;
  const softUpdatePlatformNames = softUpdatePlatforms.map((v) => PLATFORM_META[v.platform]?.label || v.platform).join(" and ");

  const openEdit   = (data)     => setModal({ open: true, data });
  const openAdd    = (platform) => setModal({ open: true, data: { platform } });
  const closeModal = ()         => setModal({ open: false, data: null });

  return (
    <div>
      <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>

      <PageHeader
        title="App Versions"
        subtitle="Manage Android and iOS app versions, force updates, maintenance mode, and store links"
      />

      {/* ── Global Maintenance Toggle ── */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <div style={{ marginLeft: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, minWidth: 300, padding: "10px 14px", borderRadius: 10, border: `1px solid ${globalMaintenanceValue ? "rgba(227,51,28,0.35)" : "var(--border)"}`, background: globalMaintenanceValue ? "rgba(228,48,25,0.06)" : "var(--bg-card)", opacity: globalMaintLoading ? 0.65 : 1 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 6 }}>
                <Wrench size={14} strokeWidth={2.5} /> Maintenance Mode
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>Common for Android and iOS</div>
            </div>
            <Toggle value={globalMaintenanceValue} onChange={handleGlobalMaintenanceToggle} disabled={globalMaintLoading} />
          </div>
        </div>
      </div>

      {/* ── Alert Banners ── */}
      {forceCount > 0 && (
        <div style={{ marginBottom: 12, padding: "12px 16px", borderRadius: 10, background: "rgba(254,242,242,0.08)", border: "1px solid rgba(239,68,68,0.25)", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#7f1d1d", fontWeight: 600 }}>
          <AlertTriangle size={15} strokeWidth={2.5} />
          Force Update is active on {forceCount} platform{forceCount > 1 ? "s" : ""}. All users must update before using the app.
        </div>
      )}
      {maintCount > 0 && (
        <div style={{ marginBottom: 12, padding: "12px 16px", borderRadius: 10, background: "rgba(228,47,37,0.08)", border: "1px solid rgba(234,179,8,0.30)", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#0b0101", fontWeight: 600 }}>
          <Wrench size={15} strokeWidth={2.5} />
          Maintenance Mode is active on {maintCount} platform{maintCount > 1 ? "s" : ""}. Users cannot access the app on {maintCount > 1 ? "these platforms" : "this platform"}.
        </div>
      )}
      {softUpdateCount > 0 && (
        <div style={{ marginBottom: 12, padding: "12px 16px", borderRadius: 10, background: "rgba(239,246,255,0.08)", border: "1px solid rgba(59,130,246,0.25)", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#1e3a8a", fontWeight: 600 }}>
          <RefreshCw size={15} strokeWidth={2.5} />
          Soft Update is active on {softUpdatePlatformNames}. Users will be prompted to update.
        </div>
      )}

      {/* ── Platform Cards ── */}
      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {[0, 1].map((i) => (
            <div key={i} style={{ height: 400, borderRadius: 14, background: "linear-gradient(90deg,var(--border) 25%,rgba(15,61,62,0.05) 50%,var(--border) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s ease-in-out infinite" }} />
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {PLATFORMS.map((p) => (
            <PlatformCard
              key={p}
              platform={p}
              data={byPlatform(p)}
              onEdit={openEdit}
              onDelete={handleDelete}
              onToggleForce={handleToggleForce}
              onToggleMaintenance={handleToggleMaintenance}
              onToggleSoftUpdate={handleToggleSoftUpdate}
              onAdd={openAdd}
            />
          ))}
        </div>
      )}

      {/* ── Info Note ── */}
      <div style={{ marginTop: 18, padding: "10px 14px", borderRadius: 8, background: "rgba(15,61,62,0.05)", border: "1px solid rgba(15,61,62,0.12)", fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 8, alignItems: "flex-start" }}>
        <Info size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>Only one version record per platform is allowed. Creating a duplicate platform returns an error. Use the Edit button to update an existing platform version.</span>
      </div>

      <VersionModal open={modal.open} onClose={closeModal} onSuccess={load} initial={modal.data} />

      <ConfirmMaintenanceModal
        open={globalMaintConfirmOpen}
        platformLabel="Android and iOS"
        onClose={() => setGlobalMaintConfirmOpen(false)}
        onConfirm={confirmGlobalMaintenanceEnable}
        loading={globalMaintLoading}
      />
    </div>
  );
}